import React, {useState} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import {ROUTE_OPTIONS} from 'Utils/constants';
import Directions from 'Components/layouts/kiosk/KioskLayout/LayoutComponents/Directions/Directions';
import DirectionsQrCodeContainer from 'Components/common/QRCodeComponents/QrCodeContainer';
import FeaturePropType from 'Utils/propTypes/FeaturePropType';
import * as analytics from 'Utils/analytics';
import useButtonStyles from 'Components/common/Events/useButtonStyles';
import LocationImage from 'Components/layouts/kiosk/KioskLayout/LayoutComponents/Locations/LocationImage';
import LocationInfo from 'Components/layouts/kiosk/KioskLayout/LayoutComponents/Locations/LocationInfo';
import LocationPromotionsContainer from 'Components/layouts/kiosk/KioskLayout/LayoutComponents/Locations/LocationPromotionsContainer';
import TenantsList from 'Components/common/Tenants/TenantsList';
import {getAncestorsDisplayText} from 'Utils/locationUtils';
import SearchCardEventsListContainer from 'Components/common/Events/SearchCardEventsListContainer';

const TABS = {
    ABOUT: 'about',
    DIRECTORY: 'directory',
};

const LocationDetailsCard = ({
    currentKioskFeature,
    hasEvents,
    location,
    selectedRouteOption,
    selectRouteOption,
    setRouteParameters,
    timeZone,
    travelTimeMins,
    tenantFeatures,
    onClickClose,
    showBackButton,
    onClickBack,
    parkingStatusMapping,
    contentSources,
    promotions,
    qrCodeConfig,
}) => {
    const classes = useButtonStyles();

    const [tab, setTab] = useState(TABS.ABOUT);

    if (!location?.properties) return <></>;

    const name = location?.properties?.tenant?.name ?? location.properties?.displayText;
    const description =
        location?.properties?.tenant?.description ?? location.properties?.description;
    const imageUrl =
        location?.properties?.tenant?.imageUrls?.medium ?? location.properties?.imageUrls?.medium;
    const showLogo = location?.properties?.tenant?.showLogo;
    const iconUrl = location?.properties?.tenant
        ? location?.properties?.tenant?.iconUrls?.url
        : location.properties?.iconUrl;
    const imdfCategoryName =
        location?.properties?.tenant?.imdfCategory?.name ??
        location?.properties?.imdfCategory?.name;
    const address = getAncestorsDisplayText(location.properties.ancestors);
    const hours = location?.properties?.tenant?.hours;
    const phone = location?.properties?.phone || location?.properties?.tenant?.phone;
    const website = location?.properties?.website || location?.properties?.tenant?.website;

    const analyticsExtraFields = {
        TenantId: location?.properties?.tenant?.uuid,
        TenantName: location?.properties?.tenant?.name,
        LocationId: location.properties.locationId,
        LocationName: location.properties.displayText,
    };

    const selectRouteOptionHandler = routeOption => {
        analytics.sendCampaignEventsEvent(
            analytics.ACTIONS.EVENT,
            {routeOption},
            analyticsExtraFields
        );
        selectRouteOption(routeOption);
        setRouteParameters({
            wheelchairOnly: routeOption === ROUTE_OPTIONS.WHEELCHAIR,
        });
    };

    const backButton =
        showBackButton && onClickBack ? (
            <div
                className={classNames('material-icons', 'btn-icon-md-18', 'btn-icon', 'm-r-8', {
                    'btn-icon-light': imageUrl,
                })}
            >
                <IconButton color="inherit" classes={{root: classes.button}} onClick={onClickBack}>
                    <ChevronLeftIcon />
                </IconButton>
            </div>
        ) : (
            false
        );

    const closeButton = onClickClose ? (
        <div
            className={classNames('material-icons', 'btn-icon-md-18', 'btn-icon', {
                'btn-icon-light': imageUrl,
            })}
        >
            <IconButton color="inherit" classes={{root: classes.button}} onClick={onClickClose}>
                <CloseIcon />
            </IconButton>
        </div>
    ) : (
        false
    );

    let content = (
        <>
            <LocationImage name={name} imageUrl={imageUrl} buttons={{backButton, closeButton}} />

            <LocationInfo
                imdfCategoryName={imdfCategoryName}
                showLogo={showLogo}
                iconUrl={iconUrl}
                name={name}
                description={location.properties.isContentSource ? undefined : description}
                address={address}
                hours={hours}
                phone={phone}
                website={website}
                timeZone={timeZone}
                parkingStatusMapping={parkingStatusMapping}
                parkingLotConfig={
                    location.properties.isParking && location.properties?.parkingLotConfig
                }
            />

            {location.properties.isContentSource &&
                location.properties.hasTenants && (
                    <div className="content-section content-shadow p-b-0">
                        <div className="content-tabs">
                            <ul className="nav-tabs">
                                <li className="nav-item">
                                    <span
                                        className={classNames('tab-link', {
                                            active: tab === TABS.ABOUT,
                                        })}
                                        aria-current="page"
                                        onClick={() => setTab(TABS.ABOUT)}
                                    >
                                        About
                                    </span>
                                </li>
                                <li className="nav-item">
                                    <span
                                        className={classNames('tab-link', {
                                            active: tab === TABS.DIRECTORY,
                                        })}
                                        aria-current="page"
                                        onClick={() => setTab(TABS.DIRECTORY)}
                                    >
                                        Explore
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}

            {tab === TABS.ABOUT && (
                <>
                    {location.properties.isContentSource &&
                        description && (
                            <div className="content-section content-shadow content-grow m-t-0 p-t-0">
                                <div className="content-body-description-wrapper">
                                    <div className="content-body-description">{description}</div>
                                </div>
                            </div>
                        )}

                    <div className="content-section content-section-last content-shadow">
                        <Directions
                            selectRouteOption={selectRouteOptionHandler}
                            selectedRouteOption={selectedRouteOption}
                            travelTimeMins={travelTimeMins}
                        />
                        <DirectionsQrCodeContainer
                            currentKioskFeature={currentKioskFeature}
                            location={location}
                            qrCodeConfig={qrCodeConfig}
                            type="Location"
                        />
                    </div>
                    {hasEvents && (
                        <div className="content-underlay glass-panel p-0 p-t-0">
                            <SearchCardEventsListContainer />
                        </div>
                    )}
                    {promotions?.length > 0 && (
                        <LocationPromotionsContainer
                            promotions={promotions}
                            tenantFeatures={tenantFeatures}
                        />
                    )}
                </>
            )}

            {tab === TABS.DIRECTORY && (
                <div className="content-body content-grow p-8 content-directory-list">
                    <TenantsList
                        isStandalone={false}
                        contentSources={contentSources}
                        directoryContentSource={location}
                        tenantFeatures={tenantFeatures}
                        currentKioskFeature={currentKioskFeature}
                    />
                </div>
            )}
        </>
    );

    if (imageUrl) {
        content = <div className="content-stack-group">{content}</div>;
    }

    return (
        <div
            className={classNames('content-container', {
                'content-no-back-button': !showBackButton,
            })}
        >
            <div
                className={classNames('content-stack', {
                    'content-has-hero-image': !!imageUrl,
                })}
            >
                {content}
            </div>
        </div>
    );
};

LocationDetailsCard.propTypes = {
    showBackButton: PropTypes.bool,
    onClickBack: PropTypes.func,
    onClickClose: PropTypes.func,
    currentKioskFeature: FeaturePropType,
    hasEvents: PropTypes.bool,
    location: PropTypes.object.isRequired,
    selectedRouteOption: PropTypes.oneOf(Object.values(ROUTE_OPTIONS)).isRequired,
    selectRouteOption: PropTypes.func.isRequired,
    setRouteParameters: PropTypes.func.isRequired,
    timeZone: PropTypes.string,
    travelTimeMins: PropTypes.number,
    tenantFeatures: PropTypes.array,
    parkingStatusMapping: PropTypes.object,
    contentSources: PropTypes.array,
    promotions: PropTypes.array,
    qrCodeConfig: PropTypes.object,
};

export default LocationDetailsCard;
